import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useToasts } from "../../contexts/toasts";
import { PrecompiledProgression, PrecompiledSet, PrecompiledWeek, PrecompiledWeekProgressionRule, SogliaItem, SogliaThreshold, SetItem } from "../../types/Entities";
import { computeNextWeekLoad, computeNextSetInSetLoad, computeMiniSetLoads, computeNextWeekReps, computeNextSetInSetReps } from "../../utils/progressionEngine";
import {
  computePrevSlotProjection,
  normalizePastedRule,
  resolveSourceSlot,
} from "../../utils/precompiledRuleApply";
// #06918ce6 Step B (B1-Detail-a) — page-level hyp-load grid uses the canonical
// walk-back-aware `computeHypLoadGrid` so Detail matches Form and the
// Programmazione embedded view by construction (Step A's synthPrev fallback
// inherits for free).
import { computeHypLoadGrid } from "../../Components/Form/ExerciseProgressionEditor";
import { simulateChain } from "../../utils/precompiledChain";
import { Entities } from "../../api/Entities";
import { Container } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faPen,
  faChartLine,
  faDumbbell,
  faCopy,
  faPaste,
  faChevronDown,
  faPlus,
  faEllipsis,
} from "@fortawesome/free-solid-svg-icons";
import { useClipboard } from "../../contexts/clipboard";
import { useEntityOptions, type EntityOptionsState } from "../../hooks/useEntityOptions";
import { Tag, Modal, Grid, Dropdown } from "antd";
import type { MenuProps } from "antd";
import ProgressionRuleEditor from "../../Components/Form/ProgressionRuleEditor";
import { TailSpin } from "react-loader-spinner";
import "./PrecompiledProgressions.css";

// ─── helpers (mirrors PrecompiledProgressionForm) ─────────────────────────────

// #4d749bac — local `computeHypLoad` collapsed into the shared
// `precompiledRuleApply.computeHypLoad`, which dispatches via
// `effectiveLoadComputeSource` (V2/3 Bug 3) so `progressionType`-only
// rules with `loadComputeSource:null` render correctly instead of
// returning null. The shared util also covers the V3/3 "both" branch
// and threads prev-slot context the same way the previous clone did.

function computeHypReps(repScheme: string | null | undefined): string | null {
  if (!repScheme) return null;
  const s = repScheme.trim().replace(/^\d+[xX]\s*/i, "");
  return s || null;
}

function ruleLabel(
  rule: PrecompiledWeekProgressionRule,
  isFirstWeek: boolean,
  isFirstSet: boolean,
): string | null {
  if (isFirstWeek && isFirstSet) {
    if (!rule.type) return null;
    if (rule.type === "fixed") {
      return rule.initialLoadType === "dynamic"
        ? `W${rule.initialWnRm ?? "?"}Rm × ${rule.initialPnRm ?? "?"}%`
        : rule.initialLoad != null ? `${rule.initialLoad} kg (fisso)` : "Fisso";
    }
    if (rule.type === "thresholds") {
      const pt = rule.progressionType;
      if (!pt || pt === "none") return "Soglie (nessuna prog.)";
      return pt === "set-to-set" ? "Soglie StS" : pt === "set-in-set" ? "Soglie SiS" : "Soglie StS+SiS";
    }
    return null;
  }
  const srcLabel =
    rule.loadComputeSource === "sts" ? "StS" :
    rule.loadComputeSource === "sis" ? "SiS" :
    rule.loadComputeSource === "both" ? "StS+SiS" :
    rule.loadComputeSource === "fixed" ? "Fisso" :
    rule.loadComputeSource === "dynamic" ? "Dinamico" : null;
  const pt = rule.progressionType;
  const ptLabel =
    pt === "set-to-set" ? "StS" :
    pt === "set-in-set" ? "SiS" :
    pt === "both" ? "StS+SiS" : null;
  if (srcLabel && ptLabel) return `${srcLabel} → prog. ${ptLabel}`;
  return srcLabel ?? ptLabel ?? null;
}

const TAG_COLORS = ["blue", "purple", "cyan", "geekblue", "volcano", "orange"];
const tagColor = (t: string) => TAG_COLORS[t.charCodeAt(0) % TAG_COLORS.length];

// Empty progression rule for a freshly-added set (mirrors the form's emptyRule).
function emptyRule(): PrecompiledWeekProgressionRule {
  return {
    type: "fixed",
    loadComputeSource: null,
    initialLoadType: "fixed",
    initialLoad: null,
    initialWnRm: null,
    initialPnRm: null,
    weekPriority: null,
    incrementDirection: null,
    incrementTarget: null,
    repsChange: null,
    loadChangeType: null,
    loadChangeValue: null,
    weekWnRm: null,
    weekPnRm: null,
    progressionType: null,
    positiveThresholdsStS: null,
    negativeThresholdsStS: null,
    positiveThresholdsSiS: null,
    negativeThresholdsSiS: null,
    matrixSource: null,
    calculationPriority: null,
    weekPrioritySiS: null,
    incrementDirectionSiS: null,
    incrementTargetSiS: null,
    repsChangeSiS: null,
    loadChangeTypeSiS: null,
    loadChangeValueSiS: null,
    weekWnRmSiS: null,
    weekPnRmSiS: null,
    matrixSourceSiS: null,
  };
}

// ─── SetDetailCard ────────────────────────────────────────────────────────────

function SetDetailCard({
  set,
  setIndex,
  isFirstWeek,
  weekRepScheme,
  hypLoad,
  prevWeekSet,
  prevSetInWeek,
  prevWeekHypLoad,
  prevSetHypLoad,
  weeks,
  wi,
  tutOptions,
  onCopyRule,
  onPasteRule,
  canPasteRule,
  onCopySet,
  onPasteSet,
  canPasteSet,
}: {
  set: PrecompiledSet;
  setIndex: number;
  isFirstWeek: boolean;
  weekRepScheme?: string | null;
  hypLoad: number | null;
  // FE-C (#546b25bd) — prev-slot context for inline pp-source-preview render.
  prevWeekSet?: PrecompiledSet | null;
  prevSetInWeek?: PrecompiledSet | null;
  prevWeekHypLoad?: number | null;
  prevSetHypLoad?: number | null;
  // PROG-ENGINE-V3/5 — full week matrix + week index for the "Sorgente carico"
  // discoverability chip on the set-card header.
  weeks: PrecompiledWeek[];
  wi: number;
  // TUT catalog to resolve a per-set `tut` id → label.
  tutOptions: { label: string; value: string | number }[];
  // Whole-rule Copia/Incolla for this set (persisted via autosave on paste).
  onCopyRule?: () => void;
  onPasteRule?: () => void;
  canPasteRule?: boolean;
  // Whole-set Copia/Incolla for this set (persisted via autosave on paste).
  onCopySet?: () => void;
  onPasteSet?: () => void;
  canPasteSet?: boolean;
}) {
  const rule = set.progressionRule;
  const hypReps = computeHypReps(weekRepScheme);
  const rl = ruleLabel(rule, isFirstWeek, setIndex === 0);
  const hasHyp = hypLoad != null || hypReps != null;
  const hasWorkData = set.workReps != null || set.workLoad != null;

  // FE-C (#546b25bd) — read-only mirror of the form's pp-source-preview.
  // Only render when the user picked StS/SiS/both as load source AND we can
  // resolve at least one projection. Silent for fixed/dynamic/none.
  const src = rule.loadComputeSource ?? null;
  const isFirstSet = setIndex === 0;
  const showSourcePreview = !(isFirstWeek && isFirstSet) && (src === "sts" || src === "sis" || src === "both");
  const stsPreview = showSourcePreview
    ? computePrevSlotProjection({
        target: "sts",
        prevWeekSet,
        prevWeekHypLoad,
        prevWeekSetRule: prevWeekSet?.progressionRule ?? null,
        weekRepScheme,
      })
    : null;
  const sisPreview = showSourcePreview
    ? computePrevSlotProjection({
        target: "sis",
        prevSetInWeek,
        prevSetHypLoad,
        prevSetRule: prevSetInWeek?.progressionRule ?? null,
        weekRepScheme,
      })
    : null;
  // FE-H (#130311b7) — extended to read {load, reps} shape per FE-H's
  // computePrevSlotProjection rewrite.
  const stsLoad = stsPreview?.load ?? null;
  const sisLoad = sisPreview?.load ?? null;
  const stsReps = stsPreview?.reps ?? null;
  const sisReps = sisPreview?.reps ?? null;
  const sourcePreviewLoad: number | null = !showSourcePreview
    ? null
    : src === "sts"
    ? stsLoad
    : src === "sis"
    ? sisLoad
    : rule.calculationPriority === "sis"
    ? (sisLoad ?? stsLoad)
    : (stsLoad ?? sisLoad);
  const sourcePreviewReps: number | string | null = !showSourcePreview
    ? null
    : src === "sts"
    ? stsReps
    : src === "sis"
    ? sisReps
    : rule.calculationPriority === "sis"
    ? (sisReps ?? stsReps)
    : (stsReps ?? sisReps);

  // Wave 1.3 (#90f3f635) — workReps-driven "futuro" projection mirroring the form.
  // Same gating/adapter as PrecompiledProgressionForm SetMiniCard.
  // FE-2 (#bf6553b3) — extended to honor `incrementTarget(SiS)` and emit a reps
  // projection (numeric or scheme string) when the rule targets reps.
  const workFuturo = (() => {
    const wr = set.workReps != null ? parseFloat(String(set.workReps)) : NaN;
    const wl = set.workLoad != null ? Number(set.workLoad) : NaN;
    if (isNaN(wr) || isNaN(wl) || wr <= 0 || wl <= 0) return undefined;
    const pt = rule.progressionType ?? null;
    if (pt === "none" || pt == null) return undefined;
    // Wave 2.1 (#0063f565) — per-confronto rule.type. SiS falls back to
    // rule.type when typeSiS is unset (back-compat).
    const ruleKindStS = rule.type;
    const ruleKindSiS = rule.typeSiS ?? rule.type;
    const isStS = (pt === "set-to-set" || pt === "both") && ruleKindStS === "thresholds";
    const isSiS = (pt === "set-in-set" || pt === "both") && ruleKindSiS === "thresholds";
    if (!isStS && !isSiS) return undefined;
    const ruleTypeStS: "%" | "weight" = rule.loadChangeType === "kg" ? "weight" : "%";
    const ruleTypeSiS: "%" | "weight" = rule.loadChangeTypeSiS === "kg" ? "weight" : "%";
    const stsTarget = rule.incrementTarget ?? "load";
    const sisTarget = rule.incrementTargetSiS ?? "load";
    const toLoadSoglie = (
      ts: SogliaThreshold[] | null | undefined,
      t: "%" | "weight",
    ): SogliaItem[] =>
      (ts ?? []).map((th) => ({ threshold: th.threshold, type: t, value: th.loadValue ?? 0 }));
    const toRepsSoglie = (ts: SogliaThreshold[] | null | undefined): SogliaItem[] =>
      (ts ?? [])
        .filter((th) => th.repsValue !== null && th.repsValue !== undefined && String(th.repsValue) !== "")
        .map((th) => ({
          threshold: th.threshold,
          type: "repScheme" as const,
          value: typeof th.repsValue === "number" ? th.repsValue : String(th.repsValue ?? ""),
        }));
    const mkLoadSynth = (target: "stsLoad" | "sisLoad"): SetItem =>
      target === "stsLoad"
        ? {
            intensityType: "%",
            progressionType: "set-to-set",
            calculationPriority: "soglie",
            workLoad: String(wl),
            repScheme: weekRepScheme ?? undefined,
            incrementRuleStS: { type: ruleTypeStS, value: 0, soglie: toLoadSoglie(rule.positiveThresholdsStS, ruleTypeStS) },
            decrementRuleStS: { type: ruleTypeStS, value: 0, soglie: toLoadSoglie(rule.negativeThresholdsStS, ruleTypeStS) },
          }
        : {
            intensityType: "%",
            progressionType: "set-in-set",
            calculationPriority: "soglie",
            workLoad: String(wl),
            repScheme: weekRepScheme ?? undefined,
            incrementRuleSiS: { type: ruleTypeSiS, value: 0, soglie: toLoadSoglie(rule.positiveThresholdsSiS, ruleTypeSiS) },
            decrementRuleSiS: { type: ruleTypeSiS, value: 0, soglie: toLoadSoglie(rule.negativeThresholdsSiS, ruleTypeSiS) },
          };
    const mkRepsSynth = (target: "stsReps" | "sisReps"): SetItem =>
      target === "stsReps"
        ? {
            intensityType: "%",
            progressionType: "set-to-set",
            calculationPriority: "soglie",
            workLoad: String(wl),
            repScheme: weekRepScheme ?? undefined,
            incrementRuleStS: { type: "repScheme", value: 0, soglie: toRepsSoglie(rule.positiveThresholdsStS) },
            decrementRuleStS: { type: "repScheme", value: 0, soglie: toRepsSoglie(rule.negativeThresholdsStS) },
          }
        : {
            intensityType: "%",
            progressionType: "set-in-set",
            calculationPriority: "soglie",
            workLoad: String(wl),
            repScheme: weekRepScheme ?? undefined,
            incrementRuleSiS: { type: "repScheme", value: 0, soglie: toRepsSoglie(rule.positiveThresholdsSiS) },
            decrementRuleSiS: { type: "repScheme", value: 0, soglie: toRepsSoglie(rule.negativeThresholdsSiS) },
          };
    let load: number | undefined;
    let loadLabel: string | undefined;
    let reps: number | string | undefined;
    let repsLabel: string | undefined;
    if (isStS) {
      if (stsTarget === "load" || stsTarget === "both") {
        const v = computeNextWeekLoad(mkLoadSynth("stsLoad"), wr);
        if (v !== undefined && load === undefined) { load = v; loadLabel = "Sett. succ."; }
      }
      if (stsTarget === "reps" || stsTarget === "both") {
        const r = computeNextWeekReps(mkRepsSynth("stsReps"), wr);
        if (r && reps === undefined) {
          reps = r.kind === "scheme" ? r.repScheme : r.reps;
          repsLabel = "Sett. succ.";
        }
      }
    }
    if (isSiS) {
      if (sisTarget === "load" || sisTarget === "both") {
        const v = computeNextSetInSetLoad(mkLoadSynth("sisLoad"), wr);
        if (v !== undefined && load === undefined) { load = v; loadLabel = "Set succ."; }
      }
      if (sisTarget === "reps" || sisTarget === "both") {
        const r = computeNextSetInSetReps(mkRepsSynth("sisReps"), wr);
        if (r && reps === undefined) {
          reps = r.kind === "scheme" ? r.repScheme : r.reps;
          repsLabel = "Set succ.";
        }
      }
    }
    if (load === undefined && reps === undefined) return undefined;
    return { load, loadLabel, reps, repsLabel };
  })();

  // PROG-ENGINE-V3/5 — "Sorgente carico" discoverability chip(s). Mirrors the
  // editor's chip — honors the V3/2 pin via resolveSourceSlot and renders one
  // chip per active axis ("both" → two chips). Silent for fixed/dynamic/null
  // and when the axis has no source slot to resolve.
  const buildSourceChip = (axis: "sts" | "sis"): string | null => {
    const resolved = resolveSourceSlot(weeks, wi, setIndex, axis, rule);
    if (!resolved) return null;
    const axisShort = axis === "sts" ? "StS" : "SiS";
    const anchor = axis === "sts"
      ? `Settimana ${resolved.sourceWi + 1} · Set ${resolved.sourceSi + 1}`
      : `Set ${resolved.sourceSi + 1}`;
    return `← ${anchor} · ${axisShort}`;
  };
  const sourceChips: string[] = !showSourcePreview
    ? []
    : src === "both"
    ? ([buildSourceChip("sts"), buildSourceChip("sis")].filter(
        (s): s is string => !!s,
      ))
    : [buildSourceChip(src as "sts" | "sis")].filter(
        (s): s is string => !!s,
      );

  return (
    <div className="ppd-set-card">
      <div className="ppd-set-header">
        <span className="ppd-set-num">{setIndex + 1}</span>
        {set.label && <span className="ppd-set-label">{set.label}</span>}
        {rl && <span className="ppd-set-rule-chip">{rl}</span>}
        {sourceChips.map((c) => (
          <span key={c} className="ppd-set-rule-chip" title={c}>
            {c}
          </span>
        ))}
      </div>

      {/* Per-set copy/paste of rule + whole set consolidated into a "⋯"
          overflow menu so the data matrix stays uncluttered. */}
      {(onCopyRule || onPasteRule || onCopySet || onPasteSet) && (
        <div className="ppd-rule-actions">
          {(() => {
            const items: MenuProps["items"] = [];
            if (onCopyRule)
              items.push({ key: "copyRule", icon: <FontAwesomeIcon icon={faCopy} />, label: "Copia regola", onClick: onCopyRule });
            if (onPasteRule)
              items.push({ key: "pasteRule", icon: <FontAwesomeIcon icon={faPaste} />, label: "Incolla regola", disabled: !canPasteRule, onClick: onPasteRule });
            if ((onCopyRule || onPasteRule) && (onCopySet || onPasteSet))
              items.push({ type: "divider" });
            if (onCopySet)
              items.push({ key: "copySet", icon: <FontAwesomeIcon icon={faCopy} />, label: "Copia set", onClick: onCopySet });
            if (onPasteSet)
              items.push({ key: "pasteSet", icon: <FontAwesomeIcon icon={faPaste} />, label: "Incolla set", disabled: !canPasteSet, onClick: onPasteSet });
            if (items.length === 0) return null;
            return (
              <Dropdown trigger={["click"]} menu={{ items }} placement="bottomRight">
                <button
                  type="button"
                  className="ppd-rule-action-btn ppd-rule-action-btn--icon"
                  title="Azioni set (copia / incolla)"
                  aria-label="Azioni set"
                >
                  <FontAwesomeIcon icon={faEllipsis} />
                </button>
              </Dropdown>
            );
          })()}
        </div>
      )}

      <div className="ppd-set-body">
        {set.effort && (
          <div className="ppd-set-field">
            <span className="ppd-set-field-label">Sforzo</span>
            <span className="ppd-set-field-value">{set.effort}</span>
          </div>
        )}
        {set.tut != null && set.tut !== "" && (
          <div className="ppd-set-field">
            <span className="ppd-set-field-label">TUT</span>
            <span className="ppd-set-field-value">
              {tutOptions.find((o) => String(o.value) === String(set.tut))?.label ??
                set.tut}
            </span>
          </div>
        )}
        {/* FE-O Item 7 — group "template" + "da sorgente" so they read as one
            cluster. The wrapper only renders if at least one branch fires. */}
        {(hypLoad != null ||
          (showSourcePreview && (sourcePreviewLoad != null || sourcePreviewReps != null))) && (
          <div className="ppd-hyp-group">
            {hypLoad != null && (
              <div className="ppd-set-field">
                <span className="ppd-set-field-label">
                  Carico ipotetico
                  <span className="ppd-set-field-sublabel">(template)</span>
                </span>
                <span className="ppd-set-field-value ppd-hyp-load">{hypLoad} kg</span>
              </div>
            )}
            {showSourcePreview && (sourcePreviewLoad != null || sourcePreviewReps != null) && (
              <div className="ppd-set-field ppd-set-field--stacked">
                <span className="ppd-set-field-label">
                  Ipotetico
                  <span className="ppd-set-field-sublabel">(da sorgente)</span>
                </span>
                <div className="pp-source-context">
                  {src === "sts" && prevWeekHypLoad != null && <>Sett. prec.: {prevWeekHypLoad} kg</>}
                  {src === "sis" && prevSetHypLoad != null && <>Set prec.: {prevSetHypLoad} kg</>}
                  {src === "both" && (
                    <>
                      {prevWeekHypLoad != null && <>Sett. prec.: {prevWeekHypLoad} kg</>}
                      {prevWeekHypLoad != null && prevSetHypLoad != null && " · "}
                      {prevSetHypLoad != null && <>Set prec.: {prevSetHypLoad} kg</>}
                    </>
                  )}
                </div>
                <div className="pp-calc-badges">
                  {sourcePreviewLoad != null && (
                    <span className="pp-calc-badge pp-calc-badge-load">{sourcePreviewLoad} kg</span>
                  )}
                  {sourcePreviewReps != null && (
                    <span className="pp-calc-badge pp-calc-badge-reps">{sourcePreviewReps} reps</span>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
        {hypReps != null && (
          <div className="ppd-set-field">
            <span className="ppd-set-field-label">Reps ipotetiche</span>
            <span className="ppd-set-field-value ppd-hyp-reps">{hypReps}</span>
          </div>
        )}
        {set.workReps != null && (
          <div className="ppd-set-field">
            <span className="ppd-set-field-label">Work Reps</span>
            <span className="ppd-set-field-value ppd-work-reps">
              {String(set.workReps)}
            </span>
          </div>
        )}
        {set.workLoad != null && (
          <div className="ppd-set-field">
            <span className="ppd-set-field-label">Work Load</span>
            <span className="ppd-set-field-value ppd-work-load">
              {set.workLoad} kg
            </span>
          </div>
        )}
        {set.trialLoad != null && (
          <div className="ppd-set-field">
            <span className="ppd-set-field-label">Carico Suggerito</span>
            <span className="ppd-set-field-value ppd-ref">{set.trialLoad} kg</span>
          </div>
        )}
        {/* Wave 3.2 (#f309a975) — mini-sets read-only with cumulative projection. */}
        {set.miniSets && set.miniSets.length > 0 && (() => {
          const baseLoad =
            (set.workLoad != null ? Number(set.workLoad) : 0) ||
            (set.trialLoad != null ? Number(set.trialLoad) : 0);
          const projections = computeMiniSetLoads(baseLoad, set.miniSets);
          return (
            <div className="ppd-set-field ppd-set-field--stacked">
              <span className="ppd-set-field-label">Mini-set (drop set)</span>
              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {set.miniSets.map((m, i) => {
                  const proj = projections[i];
                  const deltaSign = m.deltaType === "%" ? `${m.deltaValue}%` : `${m.deltaValue} kg`;
                  return (
                    <span key={i} className="ppd-set-field-value">
                      Mini #{i + 1}: {String(m.reps)} reps{proj ? ` × ${proj.load} kg` : ""} (-{deltaSign})
                    </span>
                  );
                })}
              </div>
            </div>
          );
        })()}
        {workFuturo?.load !== undefined && (
          <div className="ppd-set-field">
            <span className="ppd-set-field-label ppd-set-field-label--futuro">
              Carico ipotetico futuro (da work data) — {workFuturo.loadLabel}
            </span>
            <span className="ppd-set-field-value ppd-hyp-load">{workFuturo.load} kg</span>
          </div>
        )}
        {workFuturo?.reps !== undefined && (
          <div className="ppd-set-field">
            <span className="ppd-set-field-label ppd-set-field-label--futuro">
              Reps ipotetiche future (da work data) — {workFuturo.repsLabel}
            </span>
            <span className="ppd-set-field-value ppd-hyp-load">{workFuturo.reps} reps</span>
          </div>
        )}
        {/* Wave 2.2 (#02fc6485) — resolved type per tab. */}
        {(rule.progressionType === "set-to-set" || rule.progressionType === "both") && rule.type && (
          <div className="ppd-set-field">
            <span className="ppd-set-field-label">Set to Set</span>
            <span className="ppd-set-field-value">
              {rule.type === "thresholds" ? "Soglie" : "Fissa"}
            </span>
          </div>
        )}
        {(rule.progressionType === "set-in-set" || rule.progressionType === "both") && (rule.typeSiS ?? rule.type) && (
          <div className="ppd-set-field">
            <span className="ppd-set-field-label">Set in Set</span>
            <span className="ppd-set-field-value">
              {(rule.typeSiS ?? rule.type) === "thresholds" ? "Soglie" : "Fissa"}
            </span>
          </div>
        )}
        {/* Wave 2.3 (#e601ffe3) — rule duration, read-only when set. */}
        {rule.applyForNextWeeks != null && (
          <div className="ppd-set-field">
            <span className="ppd-set-field-label">Applica per (sett. successive)</span>
            <span className="ppd-set-field-value">{rule.applyForNextWeeks}</span>
          </div>
        )}
        {rule.applyForNextSets != null && (
          <div className="ppd-set-field">
            <span className="ppd-set-field-label">Applica per (set successivi)</span>
            <span className="ppd-set-field-value">{rule.applyForNextSets}</span>
          </div>
        )}
        {/* Wave 3.4 (#3974928e) — full chain summary, read-only.
            #972f8e54 — pass real `weeks` + `(wi, setIndex)` so simulateChain's
            V3/1 walk-back at precompiledChain.ts:147-167 can resolve a
            dependent set's source. The legacy 1-week stub starved walk-back
            and silently rendered an empty chain on dependents whose rule
            body only carried a dispatch hint. */}
        {(() => {
          const chain = simulateChain(weeks, wi, setIndex);
          if (chain.length === 0) return null;
          // FE-E (#286e266b) — main group split into Carico/Reps stacked rows;
          // use the same `pp-calc-badge-*` form factor as the form for visual
          // parity. xReps + miniSet rendered as before but on the badge form.
          const groups: Record<"main" | "xReps" | "miniSet", typeof chain> = {
            main: [], xReps: [], miniSet: [],
          };
          chain.forEach((c) => groups[c.kind].push(c));
          const mainLoad = groups.main.filter((c) => c.load != null);
          const mainReps = groups.main.filter((c) => c.reps != null);
          return (
            <div className="ppd-set-field ppd-set-field--stacked">
              <span className="ppd-set-field-label">Catena ipotetici</span>
              {groups.main.length > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: 4, width: "100%" }}>
                  {mainLoad.length > 0 && (
                    <div className="pp-chain-row">
                      <span className="pp-chain-row-label" style={{ minWidth: 44 }}>Carico</span>
                      {mainLoad.map((c, i) => (
                        <span key={`main-load-${i}`} className="pp-calc-badge pp-calc-badge-load pp-calc-badge--sm">
                          {c.label}: {c.load} kg
                        </span>
                      ))}
                    </div>
                  )}
                  {mainReps.length > 0 && (
                    <div className="pp-chain-row">
                      <span className="pp-chain-row-label" style={{ minWidth: 44 }}>Reps</span>
                      {mainReps.map((c, i) => (
                        <span key={`main-reps-${i}`} className="pp-calc-badge pp-calc-badge-reps pp-calc-badge--sm">
                          {c.label.replace(" (reps)", "")} × {c.reps} reps
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {(["xReps", "miniSet"] as const).map((k) =>
                groups[k].length === 0 ? null : (
                  <div key={k} className="pp-chain-row">
                    <span className="pp-chain-row-label" style={{ minWidth: 44 }}>
                      {k === "xReps" ? "X-reps" : "Mini"}
                    </span>
                    {groups[k].map((c, i) => (
                      <span key={`${k}-${i}`} className="pp-calc-badge pp-calc-badge-load pp-calc-badge--sm">
                        {c.label}
                        {c.load != null ? `: ${c.load} kg` : ""}
                        {c.reps != null ? ` × ${c.reps} reps` : ""}
                      </span>
                    ))}
                  </div>
                ),
              )}
            </div>
          );
        })()}
        {!hasHyp && set.trialLoad == null && !hasWorkData && !(set.miniSets && set.miniSets.length > 0) && (
          <span className="ppd-set-empty">Nessun dato ipotetico</span>
        )}
      </div>
    </div>
  );
}

// ─── WeekDetailCard ───────────────────────────────────────────────────────────

function WeekDetailCard({
  week,
  index,
  prevWeekSets,
  prevWeekHypLoads,
  hypLoads,
  weeks,
  catalogs,
  onCopySetRule,
  onPasteSetRule,
  canPasteRule,
  onCopySet,
  onPasteSet,
  canPasteSet,
  onCopyWeek,
  onPasteWeek,
  canPasteWeek,
  onAddSet,
}: {
  week: PrecompiledWeek;
  index: number;
  prevWeekSets?: PrecompiledSet[] | null;
  prevWeekHypLoads?: (number | null)[] | null;
  /** #06918ce6 Step B — pre-computed row from the page-level grid. */
  hypLoads: (number | null)[];
  /** PROG-ENGINE-V3/5 — full week matrix for the "Sorgente carico" chip. */
  weeks: PrecompiledWeek[];
  /** Catalogs to resolve type-id / tecnica / tut / rec → names in the header. */
  catalogs: EntityOptionsState;
  /** Whole-rule Copia/Incolla per set (autosaved on paste). */
  onCopySetRule: (setIndex: number) => void;
  onPasteSetRule: (setIndex: number) => void;
  canPasteRule: boolean;
  /** Whole-set Copia/Incolla per set (autosaved on paste). */
  onCopySet: (setIndex: number) => void;
  onPasteSet: (setIndex: number) => void;
  canPasteSet: boolean;
  /** Whole-week Copia/Incolla (autosaved on paste). */
  onCopyWeek: () => void;
  onPasteWeek: () => void;
  canPasteWeek: boolean;
  /** Opens the "Aggiungi set" rule modal for this week. */
  onAddSet: () => void;
}) {
  const isFirstWeek = index === 0;
  const sets = week.sets ?? [];

  // Read-only labeled chips, fixed order: Tipo Schema Reps · Rep Scheme ·
  // Tecnica · Tipo Sforzo · Sforzo · TUT · Recupero. Catalog-backed values
  // (type ids / tecnica / tut / rec) are resolved id→name.
  const resolveName = (
    opts: { label: string; value: string | number }[] | undefined,
    raw: string | number | null | undefined,
  ): string | null => {
    if (raw == null || raw === "") return null;
    const hit = opts?.find((o) => String(o.value) === String(raw));
    return hit?.label ?? String(raw);
  };
  const chips: { name: string; value: string }[] = [];
  const pushChip = (name: string, value: string | null) => {
    if (value != null && value !== "") chips.push({ name, value });
  };
  pushChip("Tipo Schema Reps", resolveName(catalogs.repSchemeTypes, week.repSchemeTypeId));
  pushChip("Rep Scheme", week.repScheme || null);
  pushChip("Tecnica", resolveName(catalogs.tecniche, week.tecnica));
  pushChip("Tipo Sforzo", resolveName(catalogs.effortTypes, week.effortTypeId));
  pushChip("Sforzo", week.effort || null);
  pushChip("TUT", resolveName(catalogs.tuts, week.tut));
  pushChip("Recupero", resolveName(catalogs.recs, week.rec));

  // #06918ce6 Step B (B1-Detail-a) — per-set hyp loads now come from the
  // page-level `computeHypLoadGrid` memo, so Detail inherits Step A's
  // synthPrev fallback and V3 walk-back without re-implementing them here.

  // FE-U (#8274a1bc) — collapsible weeks. Default: first week OPEN, rest
  // CLOSED to keep the page compact. In-memory only (no persistence) — same
  // pattern as the form's CollapsibleSection component.
  const [open, setOpen] = useState<boolean>(isFirstWeek);

  return (
    <div className={`ppd-week-card${open ? " open" : ""}`}>
      <button
        type="button"
        className="ppd-week-header"
        onClick={() => setOpen((p) => !p)}
        aria-expanded={open}
      >
        <span className="ppd-week-num">{index + 1}</span>
        <div className="ppd-week-info">
          <span className="ppd-week-title">Settimana {index + 1}</span>
          {chips.length > 0 && (
            <div className="ppd-week-chips">
              {chips.map((c) => (
                <span key={c.name} className="ppd-week-chip ppd-week-chip--labeled">
                  <span className="ppd-week-chip-name">{c.name}</span>
                  <span className="ppd-week-chip-value">{c.value}</span>
                </span>
              ))}
            </div>
          )}
        </div>
        <span className="ppd-week-set-count">{sets.length} set</span>
        <FontAwesomeIcon
          icon={faChevronDown}
          className={`ppd-week-chevron${open ? " open" : ""}`}
        />
      </button>

      {/* Whole-week Copia/Incolla consolidated into a "⋯" overflow menu. */}
      <div className="ppd-rule-actions ppd-week-actions">
        {(() => {
          const items: MenuProps["items"] = [];
          items.push({ key: "copyWeek", icon: <FontAwesomeIcon icon={faCopy} />, label: "Copia settimana", onClick: onCopyWeek });
          items.push({ key: "pasteWeek", icon: <FontAwesomeIcon icon={faPaste} />, label: "Incolla settimana", disabled: !canPasteWeek, onClick: onPasteWeek });
          return (
            <Dropdown trigger={["click"]} menu={{ items }} placement="bottomRight">
              <button
                type="button"
                className="ppd-rule-action-btn ppd-rule-action-btn--icon"
                title="Azioni settimana (copia / incolla)"
                aria-label="Azioni settimana"
              >
                <FontAwesomeIcon icon={faEllipsis} /> Settimana
              </button>
            </Dropdown>
          );
        })()}
      </div>

      {open && (
        <>
          {sets.length > 0 && (
            <div className="ppd-sets-grid">
              {sets.map((s, si) => (
                <SetDetailCard
                  key={si}
                  set={s}
                  setIndex={si}
                  isFirstWeek={isFirstWeek}
                  weekRepScheme={week.repScheme}
                  hypLoad={hypLoads[si]}
                  prevWeekSet={prevWeekSets?.[si] ?? null}
                  prevSetInWeek={si > 0 ? (sets[si - 1] ?? null) : null}
                  prevWeekHypLoad={prevWeekHypLoads?.[si] ?? null}
                  prevSetHypLoad={si > 0 ? (hypLoads[si - 1] ?? null) : null}
                  weeks={weeks}
                  wi={index}
                  tutOptions={catalogs.tuts}
                  onCopyRule={() => onCopySetRule(si)}
                  onPasteRule={() => onPasteSetRule(si)}
                  canPasteRule={canPasteRule}
                  onCopySet={() => onCopySet(si)}
                  onPasteSet={() => onPasteSet(si)}
                  canPasteSet={canPasteSet}
                />
              ))}
            </div>
          )}

          {sets.length === 0 && (
            <div className="ppd-no-sets">
              <FontAwesomeIcon icon={faDumbbell} style={{ opacity: 0.3 }} />
              <span>Nessun set configurato</span>
            </div>
          )}
          <div className="ppd-add-set-row">
            <button
              type="button"
              className="ppd-add-set-btn"
              onClick={onAddSet}
            >
              <FontAwesomeIcon icon={faPlus} /> Aggiungi set
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function PrecompiledProgressionDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast, removeToast } = useToasts();
  const {
    copyPrecompiledProgression,
    copiedPrecompiledRule,
    copyPrecompiledRule,
    copiedPrecompiledSet,
    copyPrecompiledSet,
    copiedPrecompiledWeek,
    copyPrecompiledWeek,
  } = useClipboard();
  // Catalogs to resolve type-id / tecnica / tut / rec → display names in the
  // read-only week header chips (same source the programmazione detail uses).
  const { options: catalogs } = useEntityOptions();

  // Toast with an inline "Annulla" action so a destructive paste (which
  // overwrites + persists immediately) is recoverable. `undoFn` re-applies the
  // captured prior value through the same mutation channel.
  const showUndoToast = useCallback(
    (id: string, message: string, undoFn: () => void) => {
      showToast(
        id,
        (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
            {message}
            <button
              type="button"
              onClick={() => {
                undoFn();
                removeToast(id);
              }}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                color: "var(--app-primary)",
                fontWeight: 700,
                cursor: "pointer",
                textDecoration: "underline",
              }}
            >
              Annulla
            </button>
          </span>
        ),
        undefined,
        "success",
      );
    },
    [showToast, removeToast],
  );

  const [progression, setProgression] = useState<PrecompiledProgression | null>(null);
  const [loading, setLoading] = useState(true);

  // "Aggiungi set" — staging for a brand-new set's progression rule. The new
  // set lands at index = week.sets.length; committed via the update API.
  const [addSetEdit, setAddSetEdit] = useState<{
    weekIndex: number;
    rule: PrecompiledWeekProgressionRule;
  } | null>(null);
  const [addSetSaving, setAddSetSaving] = useState(false);
  const screens = Grid.useBreakpoint();
  const isWideViewport = !!screens.md;

  useEffect(() => {
    if (!id) return;
    Entities.getPrecompiledProgression(id).then(
      ({ data }) => {
        setProgression(data.progression);
        setLoading(false);
      },
      () => {
        showToast("ppd-load-err", "Impossibile caricare la progressione", undefined, "danger");
        navigate("/progressioni-precompilate");
      }
    );
  }, [id, navigate, showToast]);

  // #06918ce6 Step B (B1-Detail-a) — collapsed onto the shared
  // `computeHypLoadGrid`. The prior naive iteration here was structurally
  // equivalent to the pre-Step-A form path and missed walk-back / synthPrev
  // bootstrap. Switching to the shared util gives Detail parity with Form
  // and the Programmazione embedded view by construction.
  const weekHypLoads = useMemo<(number | null)[][]>(
    () => (progression ? computeHypLoadGrid(progression.weeks ?? []) : []),
    [progression],
  );

  // Whole-rule Copia/Incolla per set. Copy stages the set's rule on the shared
  // clipboard slot; paste replaces the target set's rule and persists it to
  // THIS precompiled progression via the update API (autosave) — this is the
  // catalog source, so the change is meant to stick here.
  const copyRuleFromSet = (wi: number, si: number) => {
    const rule = progression?.weeks?.[wi]?.sets?.[si]?.progressionRule;
    if (!rule) return;
    copyPrecompiledRule(rule);
    showToast("ppd-rule-copied", "Regola copiata", undefined, "success");
  };
  const pasteRuleToSet = (wi: number, si: number) => {
    if (!progression || !id || !copiedPrecompiledRule) return;
    const cloned = normalizePastedRule(
      JSON.parse(JSON.stringify(copiedPrecompiledRule)),
    );
    const updatedWeeks = (progression.weeks ?? []).map((w, wIdx) =>
      wIdx !== wi
        ? w
        : {
            ...w,
            sets: (w.sets ?? []).map((s, sIdx) =>
              sIdx !== si ? s : { ...s, progressionRule: cloned },
            ),
          },
    );
    const prev = progression;
    const priorProgression = progression;
    setProgression({ ...progression, weeks: updatedWeeks }); // optimistic
    Entities.updatePrecompiledProgression(id, { weeks: updatedWeeks }).then(
      () =>
        showUndoToast("ppd-rule-pasted", "Regola incollata", () => {
          setProgression(priorProgression);
          Entities.updatePrecompiledProgression(id, { weeks: priorProgression.weeks });
        }),
      () => {
        setProgression(prev); // revert on failure
        showToast(
          "ppd-rule-paste-err",
          "Errore nel salvataggio della regola",
          undefined,
          "danger",
        );
      },
    );
  };

  // Whole-SET Copia/Incolla. Mirrors the rule copy/paste: copy stages the whole
  // set on the shared clipboard slot; paste replaces the target set and persists
  // to THIS precompiled progression via the update API (autosave).
  const copySetFromCard = (wi: number, si: number) => {
    const set = progression?.weeks?.[wi]?.sets?.[si];
    if (!set) return;
    copyPrecompiledSet(set);
    showToast("ppd-set-copied", "Set copiato", undefined, "success");
  };
  const pasteSetToCard = (wi: number, si: number) => {
    if (!progression || !id || !copiedPrecompiledSet) return;
    const cloned: PrecompiledSet = JSON.parse(JSON.stringify(copiedPrecompiledSet));
    cloned.progressionRule = normalizePastedRule(cloned.progressionRule);
    const updatedWeeks = (progression.weeks ?? []).map((w, wIdx) =>
      wIdx !== wi
        ? w
        : {
            ...w,
            sets: (w.sets ?? []).map((s, sIdx) => (sIdx !== si ? s : cloned)),
          },
    );
    const prev = progression;
    const priorProgression = progression;
    setProgression({ ...progression, weeks: updatedWeeks }); // optimistic
    Entities.updatePrecompiledProgression(id, { weeks: updatedWeeks }).then(
      () =>
        showUndoToast("ppd-set-pasted", "Set incollato", () => {
          setProgression(priorProgression);
          Entities.updatePrecompiledProgression(id, { weeks: priorProgression.weeks });
        }),
      () => {
        setProgression(prev); // revert on failure
        showToast(
          "ppd-set-paste-err",
          "Errore nel salvataggio del set",
          undefined,
          "danger",
        );
      },
    );
  };

  // Whole-WEEK Copia/Incolla. Same autosave pattern; weekNumber is re-stamped to
  // the target position and every set's rule is normalized on paste.
  const copyWeekFromCard = (wi: number) => {
    const week = progression?.weeks?.[wi];
    if (!week) return;
    copyPrecompiledWeek(week);
    showToast("ppd-week-copied", "Settimana copiata", undefined, "success");
  };
  const pasteWeekToCard = (wi: number) => {
    if (!progression || !id || !copiedPrecompiledWeek) return;
    const cloned: PrecompiledWeek = JSON.parse(JSON.stringify(copiedPrecompiledWeek));
    cloned.weekNumber = wi + 1;
    cloned.sets = (cloned.sets ?? []).map((s) => ({
      ...s,
      progressionRule: normalizePastedRule(s.progressionRule),
    }));
    const updatedWeeks = (progression.weeks ?? []).map((w, wIdx) =>
      wIdx !== wi ? w : cloned,
    );
    const prev = progression;
    const priorProgression = progression;
    setProgression({ ...progression, weeks: updatedWeeks }); // optimistic
    Entities.updatePrecompiledProgression(id, { weeks: updatedWeeks }).then(
      () =>
        showUndoToast("ppd-week-pasted", "Settimana incollata", () => {
          setProgression(priorProgression);
          Entities.updatePrecompiledProgression(id, { weeks: priorProgression.weeks });
        }),
      () => {
        setProgression(prev); // revert on failure
        showToast(
          "ppd-week-paste-err",
          "Errore nel salvataggio della settimana",
          undefined,
          "danger",
        );
      },
    );
  };

  // "Aggiungi set" — open the rule editor for a brand-new set on this week.
  const addSetFromCard = (wi: number) => {
    setAddSetEdit({ weekIndex: wi, rule: emptyRule() });
  };
  // Append a NEW set carrying the configured rule to the week and autosave.
  const commitAddSet = () => {
    if (!progression || !id || !addSetEdit) return;
    const newSet: PrecompiledSet = {
      label: null,
      trialLoad: null,
      progressionRule: addSetEdit.rule,
    };
    const updatedWeeks = (progression.weeks ?? []).map((w, wIdx) =>
      wIdx !== addSetEdit.weekIndex
        ? w
        : { ...w, sets: [...(w.sets ?? []), newSet] },
    );
    const prev = progression;
    setProgression({ ...progression, weeks: updatedWeeks }); // optimistic
    setAddSetSaving(true);
    Entities.updatePrecompiledProgression(id, { weeks: updatedWeeks }).then(
      () => {
        setAddSetSaving(false);
        setAddSetEdit(null);
        showToast("ppd-set-added", "Set aggiunto", undefined, "success");
      },
      () => {
        setProgression(prev); // revert on failure
        setAddSetSaving(false);
        setAddSetEdit(null);
        showToast(
          "ppd-set-add-err",
          "Errore nell'aggiunta del set",
          undefined,
          "danger",
        );
      },
    );
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5 my-5">
        <TailSpin height={80} width={80} color="#9c1419" radius="1" />
      </div>
    );
  }

  if (!progression) return null;

  const weeks = progression.weeks ?? [];

  // Prev-slot context for the NEW set at index = week.sets.length, so the
  // ProgressionRuleEditor's ancestor source-picker renders with real data.
  const addSetEditorCtx = (() => {
    if (!addSetEdit) return null;
    const { weekIndex } = addSetEdit;
    const week = weeks[weekIndex];
    if (!week) return null;
    const sets = week.sets ?? [];
    const setIndex = sets.length;
    const prevWeek = weekIndex > 0 ? weeks[weekIndex - 1] : null;
    const prevWeekSet = prevWeek?.sets?.[setIndex] ?? null;
    const prevSetInWeek = setIndex > 0 ? sets[setIndex - 1] ?? null : null;
    const prevWeekHypLoad =
      weekIndex > 0 ? weekHypLoads[weekIndex - 1]?.[setIndex] ?? null : null;
    const prevSetHypLoad =
      setIndex > 0 ? weekHypLoads[weekIndex]?.[setIndex - 1] ?? null : null;
    return {
      week,
      setIndex,
      prevWeekSet,
      prevSetInWeek,
      prevWeekHypLoad,
      prevSetHypLoad,
      prevWeekSetRule: prevWeekSet?.progressionRule ?? null,
      prevSetRule: prevSetInWeek?.progressionRule ?? null,
    };
  })();

  return (
    <>
      {/* ── Sticky bar ── */}
      <div className="pp-form-bar">
        <div className="d-flex align-items-center gap-2 overflow-hidden">
          <button
            className="pp-form-bar-back"
            type="button"
            onClick={() => navigate("/progressioni-precompilate")}
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
          <p className="pp-form-bar-title">{progression.label}</p>
        </div>
        <div className="pp-form-bar-actions">
          <button
            type="button"
            className="button-secondary"
            title="Copia progressione"
            onClick={() => {
              copyPrecompiledProgression(progression);
              showToast(
                "ppd-copy-ok",
                "Progressione copiata negli appunti",
                "Apri una progressione in modifica e usa Incolla.",
                "success",
              );
            }}
          >
            <FontAwesomeIcon icon={faCopy} />
            <span className="ms-2">Copia</span>
          </button>
          <button
            className="button-primary"
            title="Modifica progressione"
            onClick={() => navigate(`/progressioni-precompilate/${id}/modifica`)}
          >
            <FontAwesomeIcon icon={faPen} />
            <span className="ms-2">Modifica</span>
          </button>
        </div>
      </div>

      <Container fluid="md" className="pb-5 pt-4">
        {/* ── Info card ── */}
        <div className="ppd-info-card">
          <div className="ppd-info-icon">
            <FontAwesomeIcon icon={faChartLine} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 className="ppd-info-title">{progression.label}</h2>
            {progression.description && (
              <p className="ppd-info-desc">{progression.description}</p>
            )}
            {progression.tags?.length ? (
              <div className="ppd-info-tags">
                {progression.tags.map((t) => (
                  <Tag key={t} color={tagColor(t)} style={{ margin: 0 }}>
                    {t}
                  </Tag>
                ))}
              </div>
            ) : null}
          </div>
          <div className="ppd-info-stat">
            <span className="ppd-info-stat-num">{weeks.length}</span>
            <span className="ppd-info-stat-label">{weeks.length === 1 ? "settimana" : "settimane"}</span>
          </div>
        </div>

        {/* ── Weeks ── */}
        {weeks.length === 0 ? (
          <div className="ppd-no-weeks">Nessuna settimana configurata</div>
        ) : (
          weeks.map((week, i) => (
            <WeekDetailCard
              key={i}
              week={week}
              index={i}
              prevWeekSets={i > 0 ? weeks[i - 1].sets ?? null : null}
              prevWeekHypLoads={i > 0 ? weekHypLoads[i - 1] ?? null : null}
              hypLoads={weekHypLoads[i] ?? []}
              weeks={weeks}
              catalogs={catalogs}
              onCopySetRule={(si) => copyRuleFromSet(i, si)}
              onPasteSetRule={(si) => pasteRuleToSet(i, si)}
              canPasteRule={!!copiedPrecompiledRule}
              onCopySet={(si) => copySetFromCard(i, si)}
              onPasteSet={(si) => pasteSetToCard(i, si)}
              canPasteSet={!!copiedPrecompiledSet}
              onCopyWeek={() => copyWeekFromCard(i)}
              onPasteWeek={() => pasteWeekToCard(i)}
              canPasteWeek={!!copiedPrecompiledWeek}
              onAddSet={() => addSetFromCard(i)}
            />
          ))
        )}

        {addSetEdit && addSetEditorCtx && (
          <Modal
            open
            title={`Aggiungi set — Sett. ${addSetEdit.weekIndex + 1} · Set ${addSetEditorCtx.setIndex + 1}`}
            okText="Aggiungi"
            cancelText="Annulla"
            confirmLoading={addSetSaving}
            onOk={commitAddSet}
            onCancel={() => setAddSetEdit(null)}
            width={isWideViewport ? 900 : "100%"}
            style={isWideViewport ? { top: 24 } : { top: 0, paddingBottom: 0, maxWidth: "100%" }}
            styles={isWideViewport ? undefined : { body: { maxHeight: "75vh", overflowY: "auto" } }}
            centered={isWideViewport}
            destroyOnClose
          >
            <ProgressionRuleEditor
              rule={addSetEdit.rule}
              isFirstWeek={addSetEdit.weekIndex === 0}
              setIndex={addSetEditorCtx.setIndex}
              onChange={(partial) =>
                setAddSetEdit((prev) =>
                  prev ? { ...prev, rule: { ...prev.rule, ...partial } } : prev,
                )
              }
              weekRepScheme={addSetEditorCtx.week.repScheme}
              prevWeekHypLoad={addSetEditorCtx.prevWeekHypLoad}
              prevWeekSetRule={addSetEditorCtx.prevWeekSetRule}
              prevSetHypLoad={addSetEditorCtx.prevSetHypLoad}
              prevSetRule={addSetEditorCtx.prevSetRule}
              prevWeekSet={addSetEditorCtx.prevWeekSet}
              prevSetInWeek={addSetEditorCtx.prevSetInWeek}
              weeks={weeks}
              wi={addSetEdit.weekIndex}
              si={addSetEditorCtx.setIndex}
            />
          </Modal>
        )}
      </Container>
    </>
  );
}
